/**
 * Field formatters and display labels for the General Liabilities change-history
 * panel. Mirrors the labels and option maps used in `LiabilityPanel.tsx` so the
 * before/after diff in the history feed matches the panel's own field
 * presentation (e.g. "Aged Payables" not the raw "aged_payables" enum, currency
 * with thousands separators, dates in "27 May 2026" form).
 *
 * Used by: `LiabilityPanel.tsx` -> `<ChangeHistory />`.
 */

const fmtCurrency = (v: number): string =>
  `$${v.toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;

const fmtDate = (raw: string): string => {
  try {
    return new Date(raw).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return raw;
  }
};

const NATURE_LABELS: Record<string, string> = {
  trade: "Trade",
  lease: "Lease",
  tax: "Tax",
  utility: "Utility",
  professional_fees: "Professional Fees",
  loan: "Loan",
  insurer: "Insurer",
  other: "Other",
};

const SOURCE_LABELS: Record<string, string> = {
  aged_payables: "Aged Payables",
  invoice: "Invoice",
  contract: "Contract",
  ledger: "Ledger",
  bank_statement: "Bank Statement",
  director_disclosure: "Director Disclosure",
  proof_of_debt: "Proof of Debt",
  other: "Other",
};

const CONFIDENCE_LABELS: Record<string, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  merged: "Removed",
};

/**
 * Per-field renderers. Receive the raw stored value (string, number, boolean,
 * etc.) and return a human-readable string suitable for the diff column.
 *
 * Long-form text fields (notes, securityDetails) are intentionally not in this
 * map — `ChangeHistory.tsx` falls back to a word-level diff for any string
 * field that has no formatter, which is the right rendering for prose.
 */
export const LIABILITY_FIELD_FORMATTERS: Record<
  string,
  (value: unknown) => string
> = {
  amount: (v) =>
    typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014"),
  date: (v) => (typeof v === "string" && v ? fmtDate(v) : "\u2014"),
  natureOfDebt: (v) => NATURE_LABELS[String(v)] ?? String(v ?? "\u2014"),
  source: (v) => SOURCE_LABELS[String(v)] ?? String(v ?? "\u2014"),
  confidence: (v) => CONFIDENCE_LABELS[String(v)] ?? String(v ?? "\u2014"),
  secured: (v) => (v ? "Yes" : "No"),
  status: (v) => STATUS_LABELS[String(v)] ?? String(v ?? "\u2014"),
  creditorName: (v) =>
    typeof v === "string" && v.trim().length > 0 ? v : "\u2014",
};

/**
 * Display labels matching the panel's section / row labels, so a diff for
 * `natureOfDebt` reads "Type of debt: Trade -> Lease" instead of leaking the
 * raw schema field name to the user.
 */
export const LIABILITY_FIELD_LABELS: Record<string, string> = {
  creditorName: "Creditor",
  amount: "Amount",
  date: "As at date",
  natureOfDebt: "Type of debt",
  source: "Source",
  confidence: "Confidence",
  secured: "Secured",
  securityDetails: "Security details",
  notes: "Notes",
  status: "Status",
  sourceDocuments: "Source documents",
};
